import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useDataTable,
    useDataTableExpansion,
    type DataTableFilter,
    type DataTableRefs
} from '@odo/shared-ui';
import { useServerPatients, useInfinitePatients } from '../../../../hooks/useServerPatients';
import { useMeta } from '../../../../hooks/useWorkQueue';
import { useStatusCatalog } from '../../../../services/statusCatalog';
import { DATA_TABLE_ROW_HEIGHT } from '../../../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../../../hooks/useViewportTableLimit';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { PatientsTable } from '../../practice/patients/PatientsTable';
import type { PatientsSortBy } from '../../practice/patients/patientColumns';
import {
    joinPatientWithStatus,
    type PracticePatientRow
} from '../../practice/patients/patientStatusJoin';
import {
    buildPhaseOptions,
    groupStatusesByPhase,
    resolveInternalStatusFilter
} from '../../practice/patients/internalStatusFilter';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

interface OrthodontistPatientsTabProps {
    // Exact practice ids linked to this org - unlike PatientsTab's name-based
    // filter, this never collides across practices sharing a name.
    practiceIds: string[];
}

// Mirrors practice/patients/PatientsTab.tsx's structure, swapping the
// practice-scoped filter for the org's linked practice ids.
export function OrthodontistPatientsTab({ practiceIds }: OrthodontistPatientsTabProps) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const table = useDataTable<PatientsSortBy, { phase: string[]; internalStep: string[] }>({
        defaultSort: { by: 'patient', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { phase: [], internalStep: [] }
    });
    const { phase: phaseFilter, internalStep: stepFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';
    const expansion = useDataTableExpansion();

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef,
        remeasure
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    useLayoutEffect(() => {
        remeasure();
    }, [expansion.expanded, remeasure]);

    const { data: workflowMeta } = useMeta();
    const catalog = useStatusCatalog();
    const statuses = useMemo(() => workflowMeta?.statuses ?? [], [workflowMeta?.statuses]);
    const phases = useMemo(() => workflowMeta?.phases ?? [], [workflowMeta?.phases]);

    const internalStatus = useMemo(
        () => resolveInternalStatusFilter(phaseFilter, stepFilter, statuses),
        [phaseFilter, stepFilter, statuses]
    );

    const sharedPatientParams = {
        practiceIds,
        sort: table.sort,
        order: table.order,
        search: table.search || undefined,
        internalStatus
    };
    const paged = useServerPatients({
        ...sharedPatientParams,
        page: table.page,
        limit: table.limit,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfinitePatients({
        ...sharedPatientParams,
        limit: table.limit,
        enabled: isInfinite
    });

    const patients = isInfinite ? infinite.patients : paged.patients;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isError = isInfinite ? infinite.isError : paged.isError;
    const error = isInfinite ? infinite.error : paged.error;
    const { refresh } = paged;

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load patients'), 'error');
        }
    }, [isError, error, showToast]);

    const rows: PracticePatientRow[] = useMemo(
        () => patients.map((patient) => joinPatientWithStatus(patient, catalog)),
        [patients, catalog]
    );

    const totalPages = Math.max(1, Math.ceil(meta.total / table.limit));
    const hasFilters = phaseFilter.length > 0 || stepFilter.length > 0;
    const clearFilters = () => {
        table.setFilter('phase', []);
        table.setFilter('internalStep', []);
    };

    const openPatient = (patient: PracticePatientRow) => {
        navigate(`/patients/${patient.id}`, { state: { patientName: patient.name } });
    };

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    const phaseOptions = useMemo(() => buildPhaseOptions(phases), [phases]);
    const stepGroups = useMemo(
        () => groupStatusesByPhase(statuses, phases, catalog),
        [statuses, phases, catalog]
    );
    const filters: DataTableFilter[] = [
        {
            key: 'phase',
            label: 'Phase',
            multiple: true,
            options: phaseOptions,
            value: phaseFilter,
            onChange: (value) => table.setFilter('phase', value),
            align: 'right'
        },
        {
            key: 'internalStep',
            label: 'Internal step',
            align: 'right',
            searchable: true,
            sections: stepGroups.map((group) => ({
                key: group.phase,
                label: group.phase,
                multiple: true,
                options: group.steps,
                value: stepFilter,
                onChange: (value: string[]) => table.setFilter('internalStep', value)
            }))
        }
    ];

    return (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
            <PatientsTable
                patients={rows}
                isLoading={isLoading}
                isError={isError}
                search={table.search}
                searchConfig={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search by patient name'
                }}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                filters={filters}
                onRetry={() => void refresh()}
                onOpenPatient={openPatient}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                refs={refs}
                expansion={expansion}
            />
        </div>
    );
}
